import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import cn from 'classnames';

import beGetBillingDetails from 'actions/billing/beGetBillingDetails.action';
import beUpdateSalonBillingData from 'actions/billing/beUpdateSalonBillingData.action';
import beUpgradePlan from 'actions/billing/beUpgradePlan.action';
import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import SetupForm from 'components/billing/SetupForm.component';
import SalonModal from 'components/dialog/SalonModal.component';
import SM from 'components/dialog/SalonModal.module.scss';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import CONF from 'config/conf.singleton';
import LINK from 'constants/link.const';
import PLAN_NAME from 'constants/plan.const';
import PLAN_INDEX from 'constants/planIndex.const';
import SIGN_UP_METHOD from 'constants/signUpMethod.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonBetaAnnualSalon, selectCurrentSalonId, selectCurrentSalonName, selectCurrentSalonPricingPlan, selectCurrentSalonSignUpMethod } from 'selectors/currentSalon.selectors';
import { selectBillingCardData } from 'selectors/settings.selectors';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import linkTo from 'utils/links/linkTo.util';
import C from 'utils/values/constant.util';

import InputField from 'onboarding/common/InputField';
import BetaAnnualModal from 'settings/online-booking/modals/BetaAnnualModal';

import CN from './SubscriptionFlow.module.scss';

const ACTION = C({
  updateCustomer: 'update_customer',
  updateCard: 'update_card',
});

const SUBSCRIPTION_TYPE = C({
  create: 'create', // 'create' | 'update' | null
  update: 'update',
});

const stripePromise = loadStripe(CONF.stripeKey);
const LINK_TERMS = 'https://www.getslick.com/legal/terms';

const SubscriptionFlow = ({ plan, salonId, salonName, stripeCardData, hasBetaAnnualSalon, beUpdateSalonBillingData, beGetBillingDetails, beUpgradePlan, signUpMethod }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [clientSecret, setClientSecret] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const [isNameValid, setIsNameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { val: renderModalNextStep, yay: showModalNextStep, nay: closeModalNextStep } = useTip(false);
  const { val: renderModalSuccessSubscribe, yay: showModalSuccessSubscribe, nay: closeModalSuccessSubscribe } = useTip(false);
  const { val: renderModalSuccessUpgrade, yay: showModalSuccessUpgrade, nay: hideModalSuccessUpgrade } = useTip(false);

  const { val: renderBetaAnnualModal, yay: showBetaAnnual, nay: hideBetaAnnual } = useTip(false);
  const { val: isLoading, yay: showLoading, nay: hideLoading } = useTip(false);

  const { last4_digit: last4Digits, expiry_year: expiryYear, expiry_month: expiryMonth, is_cancelled: isCancelled } = stripeCardData;
  const isNewAccount = !last4Digits && !expiryMonth && !expiryYear;
  const shouldCreateSubscription = isNewAccount || isCancelled === true;
  const hasAccountDetails = fromAnyToBool(stripeCardData?.name && stripeCardData?.email);
  const cardNumber = last4Digits ? `XXXX XXXX XXXX ${last4Digits}` : '---';
  const cardExpiry = fromAnyToBool(expiryMonth) ? `${fromAnyToString(expiryMonth).padStart(2, '0')}/${fromAnyToString(expiryYear).substr(-2)}` : '---';
  const isNextEnabled = fromAnyToBool(name && email && !isNameValid && !isEmailValid);

  const { val: isEditingAccount, yay: editingAccount, nay: readOnlyAccount } = useTip(!hasAccountDetails);
  const { val: isEditingCard, yay: showEditingCard, nay: hideEditingCard } = useTip(false);
  const { val: isUpgrading, yay: showUpgrading, nay: hideUpgrading } = useTip(false);
  const [subscriptionSuccessType, setSubscriptionSuccessType] = useState(null); // 'create' | 'update' | null

  const linkMarketingNotification = linkTo({ pattern: LINK.reminders, params: { salonId }, fault: '#' });

  const handleName = (name) => void setName(name);
  const handleEmail = (email) => void setEmail(email);

  const handleValidateName = useCallback(
    (value) => {
      const valid = validateName({ value });
      setIsNameValid(!valid);
      return valid ? '' : t('common.errors.validation.noName');
    },
    [t],
  );

  const handleValidateEmail = useCallback(
    (value) => {
      const valid = validateEmail({ value });
      setIsEmailValid(!valid);
      return valid ? '' : t('common.errors.validation.notValidEmail');
    },
    [t],
  );

  // Step 1: Save Account Holder
  // get_customer (beGetBillingDetails)  must be called AFTER both POST requests
  const fetchAndSyncBillingDetails = useCallback(async () => {
    const result = await beGetBillingDetails({ salonId });

    const { response, error } = result || {};

    if (error || !response) {
      setErrorMessage(t('common.errors.wrongResponse'));
      return false;
    }

    const nextName = fromAnyToString(response.name);
    const nextEmail = fromAnyToString(response.email);

    setName(nextName);
    setEmail(nextEmail);
    readOnlyAccount();

    return true;
  }, [beGetBillingDetails, readOnlyAccount, salonId, t]);

  const handleSaveAccountHolder = useCallback(async () => {
    const { updateCustomer, updateCard } = ACTION;

    try {
      showLoading();
      const customerResult = await beUpdateSalonBillingData({
        salonId,
        name,
        email,
        action: updateCustomer,
      });

      if (customerResult?.error) {
        setErrorMessage(t('common.errors.wrongResponse'));
        return;
      }

      const cardResult = await beUpdateSalonBillingData({
        salonId,
        name,
        email,
        action: updateCard,
      });

      const { response, error } = cardResult || {};

      if (error || !response?.client_secret) {
        setErrorMessage(t('common.errors.wrongResponse'));
        return;
      }

      setClientSecret(response.client_secret);

      const billingDataResult = await fetchAndSyncBillingDetails();
      if (!billingDataResult) return;

      showModalNextStep();
    } catch {
      setErrorMessage(t('common.errors.generic'));
    } finally {
      hideLoading();
    }
  }, [showLoading, beUpdateSalonBillingData, salonId, name, email, fetchAndSyncBillingDetails, showModalNextStep, t, hideLoading]);

  //  post to post_customer api with update_card to read/get client_secret and setup_intent_id
  const handleStartCardSetup = useCallback(async () => {
    const { updateCard } = ACTION;

    try {
      const result = await beUpdateSalonBillingData({
        salonId,
        name,
        email,
        action: updateCard,
      });

      const { response, error } = result || {};

      if (error || !response?.client_secret) {
        setErrorMessage(t('common.errors.wrongResponse'));
        return;
      }

      setClientSecret(response.client_secret);
      showEditingCard();
    } catch {
      setErrorMessage(t('common.errors.generic'));
    }
  }, [beUpdateSalonBillingData, salonId, name, email, showEditingCard, t]);

  const handleUpgradeToAdvanced = useCallback(async () => {
    if (!salonId) return;
    // Block upgrade for Beta Annual salons
    if (hasBetaAnnualSalon) {
      showBetaAnnual();
      return;
    }
    showUpgrading();

    try {
      const result = await beUpgradePlan({
        salonId,
        planId: PLAN_INDEX.advanced,
      });

      if (result?.is?.ok) {
        closeModalSuccessSubscribe();
        showModalSuccessUpgrade();
      }
    } catch (error) {
      console.error('Error Upgrade', error);
    } finally {
      hideUpgrading();
    }
  }, [salonId, hasBetaAnnualSalon, showUpgrading, showBetaAnnual, beUpgradePlan, closeModalSuccessSubscribe, showModalSuccessUpgrade, hideUpgrading]);

  const handleSetupSuccess = useCallback(async () => {
    // NOTE: snapshot before refresh - beGetBillingDetails will update stripeCardData
    // and change isNewAccount/isCancelled, so capture the type before the fetch.
    const setupSuccessType = shouldCreateSubscription ? SUBSCRIPTION_TYPE.create : SUBSCRIPTION_TYPE.update;

    try {
      showLoading();
      await beGetBillingDetails({ salonId });

      hideEditingCard();
      setSubscriptionSuccessType(setupSuccessType);
      showModalSuccessSubscribe();
    } finally {
      hideLoading();
    }
  }, [shouldCreateSubscription, showLoading, beGetBillingDetails, salonId, hideEditingCard, showModalSuccessSubscribe, hideLoading]);

  const handleCloseModalSuccessSubscribe = useCallback(() => {
    setSubscriptionSuccessType(null);
    closeModalSuccessSubscribe();
  }, [closeModalSuccessSubscribe]);

  useEffect(() => {
    const nextName = fromAnyToString(stripeCardData?.name);
    const nextEmail = fromAnyToString(stripeCardData?.email);

    if (nextName || nextEmail) {
      setName(nextName);
      setEmail(nextEmail);
      readOnlyAccount(); //  read-only if name and email
    } else {
      editingAccount(); // first
    }
  }, [editingAccount, readOnlyAccount, stripeCardData]);

  return (
    <div
      data-bem="SubscriptionFlow"
      className={cn(CN.component, {
        [CN.dark]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}
      {/* Step 1: Account  */}
      <div data-bem="SubscriptionFlow__account">
        <div
          className={cn({
            [CN.component]: true,
            [CN.dark]: isDarkMode,
          })}
        >
          <div className={CN.section}>
            <div className={CN.inner}>
              <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.section1Title')}</div>
              {/* Read only mode */}
              {!isEditingAccount && (
                <div data-bem="SubscriptionFlow__read-only-mode">
                  <div className={CN.container}>
                    <div className={CN.inputContainer}>
                      <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountNameLabel')}</div>
                      <div className={CN.value}>{name || '--'}</div>
                    </div>
                    <div className={CN.inputContainer}>
                      <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountEmailLabel')}</div>
                      <div className={CN.value}>{email || '--'}</div>
                    </div>
                  </div>

                  <div className={CN.container}>
                    <div className={CN.inputContainer}>
                      <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.businessLabel')}</div>
                      <div className={CN.value}>{salonName || '--'}</div>
                    </div>
                    <div className={CN.inputContainer}>
                      <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountIdLabel')}</div>
                      <div className={CN.value}>{salonId || '--'}</div>
                    </div>
                  </div>
                  <div className={CN.buttonContainer}>
                    <Button onClick={editingAccount} variant="primary" xSpace>
                      {t('staffAndBusiness.subAndBilling.billingDetails.editButton')}
                    </Button>
                  </div>
                </div>
              )}
              {/* Edit account mode */}
              {isEditingAccount && (
                <div data-bem="SubscriptionFlow__edit-mode">
                  <div className={CN.container}>
                    <div className={CN.inputContainer}>
                      <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountNameLabel')}</div>
                      <InputField
                        data={{
                          id: 'NameInput',
                          inputValue: name,
                          placeholder: t('staffAndBusiness.subAndBilling.billingDetails.accountNamePlaceholder'),
                          type: 'text',
                        }}
                        onChangeHandler={handleName}
                        validateBlur={handleValidateName}
                      />
                    </div>

                    <div className={CN.inputContainer}>
                      <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountEmailLabel')}</div>
                      <InputField
                        data={{
                          id: 'EmailInput',
                          inputValue: email,
                          placeholder: t('staffAndBusiness.subAndBilling.billingDetails.accountEmailPlaceholder'),
                          type: 'text',
                        }}
                        onChangeHandler={handleEmail}
                        validateBlur={handleValidateEmail}
                      />
                    </div>
                  </div>
                  <div className={CN.buttonContainer}>
                    <Button tag="SubscriptionFlow__save-account-holder" disabled={!isNextEnabled} onClick={handleSaveAccountHolder} variant="primary" xSpace>
                      {hasAccountDetails ? t('staffAndBusiness.subAndBilling.billingDetails.saveChangesButton') : t('staffAndBusiness.subAndBilling.billingDetails.addButton')}
                    </Button>
                  </div>
                </div>
              )}

              {errorMessage && <p className="errorMessage">{errorMessage}</p>}
            </div>
          </div>
        </div>
      </div>
      {/*  Step 2: Card  */}
      {hasAccountDetails && (
        <div data-bem="SubscriptionFlow__card" className={CN.section}>
          <div className={CN.inner}>
            <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.section2Title')}</div>
            <div className={CN.text}>
              <p>{t('staffAndBusiness.subAndBilling.billingDetails.section2Description1')}</p>
              <div>
                {t('staffAndBusiness.subAndBilling.billingDetails.section2Description2')}
                <ExternalLink link={LINK_TERMS} variant="inline">
                  <div className={CN.link}>{LINK_TERMS}</div>
                </ExternalLink>
              </div>
              <p>{t('staffAndBusiness.subAndBilling.billingDetails.section2Description3')}</p>
            </div>
            {!isEditingCard && (
              <div data-bem="SubscriptionFlow__card-read-only-mode">
                <div className={CN.container}>
                  <div className={CN.inputContainer}>
                    <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.cardNumberLabel')}</div>
                    <div className={CN.value}>{cardNumber}</div>
                  </div>
                  <div className={CN.inputContainer}>
                    <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.cardExpiryLabel')}</div>
                    <div className={CN.value}>{cardExpiry}</div>
                  </div>
                </div>
                <div className={CN.buttonContainer}>
                  <Button variant="primary" onClick={handleStartCardSetup}>
                    <div className={CN.buttonGap}>
                      {fromAnyToBool(last4Digits) && fromAnyToBool(expiryMonth)
                        ? t('staffAndBusiness.subAndBilling.billingDetails.editCardButton')
                        : t('staffAndBusiness.subAndBilling.billingDetails.addCardButton')}
                    </div>
                  </Button>
                </div>
              </div>
            )}

            {isEditingCard && clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
                <SetupForm handleGoBack={() => hideEditingCard()} salonId={salonId} onSuccess={handleSetupSuccess} createSubscription={shouldCreateSubscription} />
              </Elements>
            )}
          </div>
        </div>
      )}
      {/* Step 1 Account Success Modal */}
      <SalonModal tag="SubscriptionFlow__account-success" isOpen={renderModalNextStep} withFooter>
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={closeModalNextStep}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>

        <SalonModal.Body>
          <IconStatus variant="success">
            <Icon variant="confirm" width="lg" />
          </IconStatus>

          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>
              {isNewAccount ? t('staffAndBusiness.subAndBilling.billingDetails.successModalMsg1') : t('staffAndBusiness.subAndBilling.billingDetails.successModalMsg2')}
            </div>
            {isNewAccount && <div className={SM.modalText}> {t('staffAndBusiness.subAndBilling.billingDetails.successModalText')}</div>}
          </div>
        </SalonModal.Body>

        <SalonModal.Footer>
          <Button onClick={closeModalNextStep} variant="primary" noMargin xSpace>
            {isNewAccount ? t('staffAndBusiness.subAndBilling.billingDetails.nextButton') : t('staffAndBusiness.subAndBilling.billingDetails.closeButton')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>

      {/* Step 2 Success Subscription */}
      <SalonModal tag="SubscriptionFlow__subscription-success" isOpen={renderModalSuccessSubscribe} withFooter>
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={handleCloseModalSuccessSubscribe}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>

        <SalonModal.Body>
          <IconStatus variant="success">
            <Icon variant="confirm" width="lg" />
          </IconStatus>

          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>
              {subscriptionSuccessType === SUBSCRIPTION_TYPE.create
                ? t('staffAndBusiness.subAndBilling.billingDetails.activatedTitle')
                : t('staffAndBusiness.subAndBilling.billingDetails.cardUpdatedTitle')}
            </div>
            {subscriptionSuccessType === SUBSCRIPTION_TYPE.create && (
              <>
                <div className={SM.modalText}>
                  <Space bold>
                    {t('staffAndBusiness.subAndBilling.billingDetails.planLabel')}
                    {t('staffAndBusiness.subAndBilling.billingDetails.essentialPlan')}
                    {signUpMethod === SIGN_UP_METHOD.self && t('staffAndBusiness.subAndBilling.billingDetails.digitalCardsAddon')}
                  </Space>
                </div>

                <div className={SM.modalText}>
                  {S.party} {t('staffAndBusiness.subAndBilling.billingDetails.includesText')}
                  {signUpMethod === SIGN_UP_METHOD.self && <Space>{t('staffAndBusiness.subAndBilling.billingDetails.confirmationsAddon')}</Space>}
                </div>

                <div className={CN.alertOuter}>
                  <AlertPanel variant="alert">{t('staffAndBusiness.subAndBilling.billingDetails.billingNote')}</AlertPanel>
                </div>
              </>
            )}
          </div>
        </SalonModal.Body>

        <SalonModal.Footer>
          {(plan === PLAN_NAME.essential || subscriptionSuccessType === SUBSCRIPTION_TYPE.create) && (
            <div data-bem="SubscriptionFlow__modal-footer-button" className={CN.modalButtons}>
              <NavLink to={linkMarketingNotification}>
                <span className={CN.navLink}>{t('staffAndBusiness.subAndBilling.billingDetails.smsLinkNotifications')}</span>
              </NavLink>
              <Button tag="SubscriptionFlow__upgrade-to-advance" variant="primary" onClick={handleUpgradeToAdvanced} disabled={isUpgrading}>
                {isUpgrading ? t('composite.confirmation.cardsModal.upgradingButton') : t('composite.confirmation.cardsModal.upgradeButton')}
              </Button>
            </div>
          )}
        </SalonModal.Footer>
      </SalonModal>
      {renderBetaAnnualModal && <BetaAnnualModal planName="Essential" isOpen onClose={hideBetaAnnual} />}

      <SalonModal tag="SubscriptionFlow_modal-success-advanced" isOpen={renderModalSuccessUpgrade} variant="noFooter">
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={hideModalSuccessUpgrade}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus width="lg" variant="success">
            <Icon selected width="l1" variant="confirm" />
          </IconStatus>

          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.successMsgAfterUpgradeToAdvance')}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

const SubscriptionFlowHoc = connect(
  (state) => ({
    stripeCardData: selectBillingCardData(state),
    signUpMethod: selectCurrentSalonSignUpMethod(state),
    plan: selectCurrentSalonPricingPlan(state),
    salonId: selectCurrentSalonId(state),
    salonName: selectCurrentSalonName(state),
    hasBetaAnnualSalon: selectCurrentSalonBetaAnnualSalon(state),
  }),
  mapActionsToDispatchToProps({
    beGetBillingDetails,
    beUpdateSalonBillingData,
    beUpgradePlan,
  }),
)(SubscriptionFlow);

export default SubscriptionFlowHoc;
